import type { SecurePermissions } from "@webuild-ai/react-auth";

import { useEffect, useMemo, useState } from "react";
import { usePermissions, useRBAC } from "@webuild-ai/react-auth";

/** Signature of the RBAC resource/action check exposed by `useRBAC()`. */
export type HasRBACPermission = (resource: string, action: string) => boolean;

/**
 * What the current user may do, named once so every surface asks the same
 * question the same way.
 *
 * Two permission systems back these flags and they do not mean the same thing:
 *
 * - `usePermissions()` returns the five coarse `SecurePermissions` flags, which
 *   admin computes from RBAC **read** grants in its `verify-permissions`
 *   endpoint (`workflows` is true for `workflows:read`, `workflows:admin` or
 *   `*:admin`). They answer "may this user see the feature at all".
 * - `useRBAC()` returns the granular resource/action grants, which is the only
 *   thing that distinguishes reading a workflow from authoring one.
 *
 * Deriving the two independently at each call site is what let the sidebar, the
 * landing tiles, the Library tabs and the route guards drift apart, so they all
 * read this instead.
 */
export interface Capabilities {
  /** Full system administrator (`*:admin`). */
  isAdmin: boolean;
  /** May see workflows listed at all. */
  canViewWorkflows: boolean;
  /** May open the Workflow Builder and save changes to a workflow. */
  canAuthorWorkflows: boolean;
  /** May bring a new workflow into existence, including by cloning one. */
  canCreateWorkflows: boolean;
  /** May see the saved prompt library. */
  canViewPrompts: boolean;
  /** May reach the Knowledge Hub. */
  canViewKnowledgeHub: boolean;
  /** May reach the monitoring and compliance dashboards. */
  canViewMonitoring: boolean;
  /** May create, edit or delete tenant skills. */
  canManageSkills: boolean;
  /**
   * True once the RBAC grants have landed, so an RBAC-derived denial is
   * trustworthy rather than merely "not fetched yet". Only the RBAC-backed
   * fields need this: `PrivateRoute` holds the whole app on the loading page
   * until the `verify-permissions` call has resolved, so the
   * `SecurePermissions` flags are already settled before any route renders.
   */
  isSettled: boolean;
}

/** Capability keys a route guard may require. `isSettled` is not a gate. */
export type CapabilityKey = keyof Omit<Capabilities, "isSettled">;

/**
 * Pure builder, so the permission matrix can be tested directly rather than
 * through `renderHook` — the same reason `buildSecureMenuItems` is exported
 * alongside `useSidebarMenuItems`.
 */
export const buildCapabilities = (
  permissions: SecurePermissions,
  hasPermission: HasRBACPermission,
): Omit<Capabilities, "isSettled"> => {
  const isAdmin = hasPermission("*", "admin");
  const isWorkflowAdmin = isAdmin || hasPermission("workflows", "admin");

  return {
    isAdmin,
    canViewWorkflows: Boolean(permissions.workflows),
    // Authoring covers both editing an existing workflow and creating one, so
    // the Workflow Builder opens for either grant. Read alone is not enough:
    // the Builder saves.
    canAuthorWorkflows:
      isWorkflowAdmin ||
      hasPermission("workflows", "write") ||
      hasPermission("workflows", "create"),
    canCreateWorkflows: isWorkflowAdmin || hasPermission("workflows", "create"),
    canViewPrompts: Boolean(permissions.prompt_library),
    canViewKnowledgeHub: Boolean(permissions.knowledge_hub),
    canViewMonitoring: Boolean(permissions.monitoring),
    canManageSkills: isAdmin,
  };
};

/**
 * Whether the user may edit one specific workflow.
 *
 * A tenant-wide grant is accepted as well as a per-workflow one because
 * react-auth's `permissionMatches` only wildcard-matches on a `":*"` resource
 * suffix: a user holding `{resource: "workflows", action: "write"}` fails
 * `hasPermission("workflows:<id>", "write")` outright. Checking the per-workflow
 * resource alone therefore left the Manager and Content Creator roles with no
 * edit action at all, and made these checks pass only for `*:admin`.
 */
export const canEditWorkflow = (hasPermission: HasRBACPermission, workflowId: string): boolean =>
  hasPermission("*", "admin") ||
  hasPermission("workflows", "admin") ||
  hasPermission("workflows", "write") ||
  hasPermission(`workflows:${workflowId}`, "write");

/** Whether the user may delete one specific workflow. See `canEditWorkflow`. */
export const canDeleteWorkflow = (hasPermission: HasRBACPermission, workflowId: string): boolean =>
  hasPermission("*", "admin") ||
  hasPermission("workflows", "admin") ||
  hasPermission("workflows", "delete") ||
  hasPermission(`workflows:${workflowId}`, "delete");

/**
 * The single source of truth for what the current user may do. Prefer this over
 * reading `usePermissions()` or `useRBAC()` directly in a component.
 */
export const useCapabilities = (): Capabilities => {
  const { permissions } = usePermissions();
  const { hasPermission, isLoading: isRbacLoading } = useRBAC();

  // `RBACProvider` starts at `isLoading: false` with an empty grant list and
  // only flips to `true` inside its own mount effect, so on the very first
  // render "not loading" is indistinguishable from "not started". Effects flush
  // child-first, so this one runs before the provider's; waiting a single commit
  // before reading `isRbacLoading` lets the provider's state land first.
  //
  // Without the wait, a route guard reading an RBAC-backed capability denies on
  // the first render and redirects an entitled admin to /access-denied before
  // their grants ever arrive. Same trick, same reason, as `useLibrarySkills`.
  const [hasRenderedOnce, setHasRenderedOnce] = useState(false);

  useEffect(() => {
    setHasRenderedOnce(true);
  }, []);

  return useMemo(
    () => ({
      ...buildCapabilities(permissions, hasPermission),
      isSettled: hasRenderedOnce && !isRbacLoading,
    }),
    [permissions, hasPermission, hasRenderedOnce, isRbacLoading],
  );
};
